// Browser-local persistence for table preferences. Every read and write is wrapped: storage
// can be missing or throw (a private window, blocked site data), and a table must still
// render with its defaults when it does.
export const readStoredState = <T, >(key: string, fallback: T): T => {
  try {
    const stored = localStorage.getItem(key)
    return stored ? JSON.parse(stored) : fallback
  } catch {
    return fallback
  }
}

export const writeStoredState = (key: string, value: unknown) => {
  try {
    localStorage.setItem(key, JSON.stringify(value))
    return true
  } catch {
    return false
  }
}

export const pruneStoredState = <T extends Record<string, unknown>>(
  value: T,
  allowedIds: Set<string>,
) => Object.fromEntries(
  Object.entries(value).filter(([id]) => allowedIds.has(id)),
) as T

export const normalizeColumnOrder = (
  storedValue: unknown,
  allowedColumnIds: ReadonlySet<string>,
  canonicalOrder: readonly string[],
): string[] => {
  const permittedCanonicalOrder = canonicalOrder.filter((id) => allowedColumnIds.has(id))
  if (!Array.isArray(storedValue)) return permittedCanonicalOrder

  const seen = new Set<string>()
  const normalized = storedValue.reduce<string[]>((order, id) => {
    if (typeof id === 'string' && allowedColumnIds.has(id) && !seen.has(id)) {
      seen.add(id)
      order.push(id)
    }
    return order
  }, [])

  permittedCanonicalOrder.forEach((id) => {
    if (!seen.has(id)) normalized.push(id)
  })

  return normalized
}
